import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SERVICES } from "../lib/content";
import SkylineDivider from "../components/site/SkylineDivider";
import ServiceMarker from "../components/site/ServiceMarker";
import { HexMark } from "../components/site/LogoMotif";

const SIGNATURE_BY_SERVICE = {
  "commercial-proof": "proof-signature",
  "commercial-arm": "arm-signature",
  "commercial-build": "build-signature",
};

function ServiceSection({ service, tone, index }) {
  const isDark = tone === "dark";
  return (
    <section
      id={service.key}
      data-testid={`services-section-${service.key}`}
      className={`py-28 md:py-40 relative overflow-hidden ${
        isDark ? "bg-kingdom text-paper" : "bg-cream text-charcoal"
      }`}
    >
      {isDark && <div className="arch-depth"></div>}
      {!isDark && <div className="najdi-bg" style={{ opacity: 0.045 }}></div>}

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
        <div className="grid md:grid-cols-12 gap-14">
          <div className="md:col-span-5">
            <div className="mb-10 flex items-center gap-4">
              <ServiceMarker name={SIGNATURE_BY_SERVICE[service.key]} />
              <div className={`h-[1px] flex-1 ${isDark ? "bg-gold/30" : "bg-charcoal/15"}`}></div>
            </div>
            <span className={`eyebrow ${isDark ? "" : "eyebrow-dark"}`}>
              Path 0{index + 1}
            </span>
            <h2
              className={`font-display text-4xl md:text-5xl mt-6 leading-[1.05] ${
                isDark ? "text-paper" : "text-charcoal"
              }`}
            >
              {service.name}
            </h2>
            <p className="mt-6 font-display text-xl italic text-gold">
              {service.tagline}
            </p>
          </div>
          <div
            className={`md:col-span-6 md:col-start-7 text-[16px] leading-relaxed ${
              isDark ? "text-warm/85" : "text-charcoal/80"
            }`}
          >
            <p>{service.body}</p>
            {service.outcome && (
              <p
                className={`mt-8 pt-8 border-t ${
                  isDark ? "border-white/10" : "border-charcoal/15"
                } italic font-display text-lg`}
              >
                {service.outcome}
              </p>
            )}
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {service.tiles.map((t, i) => (
            <div
              key={t.title}
              data-testid={`service-tile-${service.key}-${i}`}
              className={`p-7 md:p-8 border transition-all duration-700 min-h-[240px] group relative ${
                isDark
                  ? "bg-kingdom/70 border-white/8 hover:border-gold/50"
                  : "bg-white border-charcoal/10 hover:border-gold"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="opacity-75 group-hover:opacity-100 transition-opacity duration-500">
                  <ServiceMarker name={t.marker} />
                </div>
                <span className="font-display text-sm text-gold">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h4
                className={`font-display text-xl mt-8 leading-snug ${
                  isDark ? "text-paper" : "text-charcoal"
                }`}
              >
                {t.title}
              </h4>
              <p
                className={`mt-4 text-[14px] leading-relaxed ${
                  isDark ? "text-warm/75" : "text-charcoal/70"
                }`}
              >
                {t.body}
              </p>
            </div>
          ))}
        </div>
      </div>

      {isDark && (
        <SkylineDivider
          variant="gold"
          opacity={0.14}
          height={140}
          className="skyline-bottom"
        />
      )}
    </section>
  );
}

export default function Services() {
  return (
    <div>
      {/* HERO with Riyadh skyline depth */}
      <section
        data-testid="services-hero"
        className="kingdom-fade text-paper py-32 md:py-44 relative overflow-hidden"
      >
        <div className="skyline"></div>
        <div className="ambient-glow"></div>
        <div className="skyline-sunrise"></div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="mb-10"><HexMark size={22} /></div>
          <span className="eyebrow">Services</span>
          <h1 className="font-display text-4xl sm:text-5xl md:text-[72px] leading-[1.02] mt-8 max-w-4xl">
            Start with what <br />
            <span className="italic text-gold">the market justifies.</span>
          </h1>
          <p className="mt-10 text-lg md:text-xl text-warm/85 leading-relaxed max-w-3xl font-light">
            HuddleUp is structured around three engagement paths for companies
            entering the Kingdom. Some need evidence before they commit. Some
            need local commercial coverage while the market develops. Some are
            ready to build the operating layer around a real opportunity.
          </p>
        </div>

        <SkylineDivider variant="gold" opacity={0.22} height={160} className="skyline-bottom" />
      </section>

      {/* ENGAGEMENT MODEL — three product worlds */}
      <section
        data-testid="services-engagement-model"
        className="bg-obsidian text-paper py-28 md:py-36 relative overflow-hidden"
      >
        <div className="najdi-bg-dark" style={{ opacity: 0.05 }}></div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="eyebrow">Engagement Model</span>
            <h2 className="font-display text-3xl md:text-5xl mt-6 leading-tight">
              Three engagement paths, <br />
              <span className="italic text-gold">one commercial arm.</span>
            </h2>
          </div>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-[60px] left-[16%] right-[16%] h-[1px] bg-gradient-to-r from-transparent via-gold/50 to-transparent"></div>

            {SERVICES.map((s, i) => (
              <a
                key={s.key}
                href={`#${s.key}`}
                data-testid={`engagement-node-${s.key}`}
                className="group flex flex-col items-center text-center relative"
              >
                <div className="relative bg-obsidian z-10 px-6">
                  <div className="w-28 h-28 border border-gold/40 flex items-center justify-center transition-all duration-700 group-hover:border-gold group-hover:bg-kingdom">
                    <ServiceMarker name={SIGNATURE_BY_SERVICE[s.key]} />
                  </div>
                </div>
                <span className="eyebrow mt-8">Path 0{i + 1}</span>
                <h3 className="font-display text-2xl md:text-3xl mt-4 text-paper leading-tight">
                  {s.name}
                </h3>
                <p className="mt-4 text-sm text-warm/70 leading-relaxed max-w-[240px]">
                  {s.tagline}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {SERVICES.map((s, i) => (
        <ServiceSection
          key={s.key}
          service={s}
          index={i}
          tone={i % 2 === 0 ? "light" : "dark"}
        />
      ))}

      {/* CTA */}
      <section
        data-testid="services-cta"
        className="bg-obsidian text-paper py-28 md:py-40 relative overflow-hidden"
      >
        <div className="ambient-glow"></div>
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center relative z-10">
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05]">
            Find your first move <br />
            <span className="italic text-gold">in the Kingdom.</span>
          </h2>
          <div className="mt-12 flex justify-center">
            <Link to="/diagnostic" data-testid="services-cta-button" className="btn-gold-lg">
              Find Your First Move
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
        <SkylineDivider variant="gold" opacity={0.14} height={140} className="skyline-bottom" />
      </section>
    </div>
  );
}
